/* eslint-disable react/jsx-boolean-value */
import React from 'react';

import type { ExtensionHandler, ExtensionHandlers } from '@atlaskit/editor-common/extensions';

import {
	getMacroAttributesFromADFNode,
	RENDERER,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import type { Mode } from '@confluence/macro-tracker/entry-points/types.ts';
import {
	EXTENSION_TYPE,
	MIGRATION_EXTENSION_KEYS,
	EXTENSION_KEY,
} from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { LegacyMacroRenderer } from '@confluence/fabric-extension-legacy-macro-renderer/entry-points/LegacyMacroRendererQuery.ts';
import { fg } from '@confluence/feature-gating';
import {
	REDACTION_EXTENSION_TYPE,
	TourableRedactionExtensionLoader,
} from '@confluence/redactions/entry-points/extension.ts';

import { supportedMacroExtensions } from './commentExtensionConstants.ts';
import { FallbackComponent } from './FallbackComponent.tsx';
import { UnsupportedFallbackComponent } from './UnsupportedFallbackComponent.tsx';
import type { ExtensionHandlerProps } from './commentExtensionTypes.ts';

type GetMacroExtensionHandlerFn = (input: {
	setLegacyRendererFlag?: (extensionKey?: string | undefined) => void;
	contentId?: string;
	spaceKey?: string;
	mode?: Mode;
}) => ExtensionHandler<any>;

type GetMigrationExtensionHandlerFn = (
	setLegacyRendererFlag?: ((extensionKey?: string | undefined) => void) | undefined,
	contentId?: string,
) => ExtensionHandler<any>;

const getRedactionExtensionHandler: (contentId?: string) => ExtensionHandler<any> =
	(contentId) => (extension) => {
		return (
			<TourableRedactionExtensionLoader
				contentId={contentId}
				extensionNode={extension}
				isCommentContext={true}
			/>
		);
	};

export const getExtensionHandlers = (
	setLegacyRendererFlag?: (extensionKey?: string | undefined) => void,
	contentId?: string,
	spaceKey?: string,
	mode?: Mode,
): ExtensionHandlers => {
	const macroHandler: object = {
		[EXTENSION_TYPE.MACRO_CORE]: getMacroExtensionHandler({
			setLegacyRendererFlag,
			contentId,
			spaceKey,
			mode,
		}),
	};

	const migrationHandler: object = {
		[EXTENSION_TYPE.MIGRATION]: getMigrationExtensionHandler(setLegacyRendererFlag, contentId),
	};

	const redactionHandler: object = fg('confluence_dlp_comment_redaction')
		? {
				[REDACTION_EXTENSION_TYPE]: getRedactionExtensionHandler(contentId),
			}
		: {};

	return {
		...macroHandler,
		...migrationHandler,
		...redactionHandler,
	};
};

const getMacroExtensionHandler: GetMacroExtensionHandlerFn =
	({ setLegacyRendererFlag, contentId, spaceKey, mode }) =>
	(extension) => {
		const { extensionKey } = extension;

		if (setLegacyRendererFlag) {
			setLegacyRendererFlag(extensionKey);
		}

		if (supportedMacroExtensions.includes(extensionKey)) {
			const attributes = getMacroAttributesFromADFNode(extension);
			const macroDefaultProps: ExtensionHandlerProps = {
				node: extension,
				contentId: contentId || '',
				mode: mode || RENDERER,
				spaceKey: spaceKey || '',
				extensionKey,
				attributes,
			};
			return <LegacyMacroRenderer {...macroDefaultProps} />;
		}

		return <UnsupportedFallbackComponent />;
	};

const getMigrationExtensionHandler: GetMigrationExtensionHandlerFn =
	(setLegacyRendererFlag, contentId) => (extension) => {
		const { extensionKey } = extension;

		if (extensionKey === MIGRATION_EXTENSION_KEYS.INVALID) {
			if (setLegacyRendererFlag) setLegacyRendererFlag();
			return <UnsupportedFallbackComponent />;
		}

		if (
			extensionKey === EXTENSION_KEY.INLINE_EXTERNAL_IMAGE ||
			extensionKey === EXTENSION_KEY.INLINE_MEDIA_IMAGE
		) {
			if (setLegacyRendererFlag) setLegacyRendererFlag(extensionKey);
			return contentId ? <FallbackComponent contentId={contentId} /> : null;
		}

		return null;
	};
